import React from 'react';
import type { ClarifyingQuestion } from '../../types';

interface ClarifyChipsProps {
  questions: ClarifyingQuestion[];
  /**
   * Called with the chosen option text (or a skip sentinel). The parent
   * auto-sends this as the next chat turn — a single action, NOT an input
   * prefill. (AC-1645)
   */
  onAnswer: (text: string) => void;
  disabled?: boolean;
}

/**
 * ClarifyChips (AC-1645, epic AC-1639). Renders the assistant's clarifying
 * questions as one-click answer chips so the user can resolve missing details
 * before a plan is built, without typing free text. Each option is a real
 * <button> (keyboard-accessible); clicking one answers the group in a single
 * action. A "use default" control lets the user continue without picking an
 * explicit option. The whole group is a labelled role="group" region so screen
 * readers announce its purpose.
 */
const ClarifyChips: React.FC<ClarifyChipsProps> = ({
  questions,
  onAnswer,
  disabled = false,
}) => {
  return (
    <div
      role="group"
      aria-label="To continue, choose:"
      className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-3"
    >
      {questions.map((q) => (
        <div key={q.field} className="space-y-1.5">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {q.question}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(q.options ?? []).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => onAnswer(option)}
                disabled={disabled}
                className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded hover:bg-blue-200 dark:hover:bg-blue-900/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onAnswer('Use the default')}
        disabled={disabled}
        className="text-xs text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Use default
      </button>
    </div>
  );
};

export default ClarifyChips;
